import type { AuroraInitializationData } from '@wisesystems/aurora-ui';
import type { RootState } from '~/store';
import { selectCurrentUser } from '~/reducers/currentUserSlice';
import { selectClientIds } from '~/reducers/clientsSlice';
import { selectSelectedClientIds } from '~/reducers/selectedClientsSlice';
import constants from '~/utils/constants';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonemptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

/** Read Aurora's context on demand without changing MCW's session handling. */
export const getAuroraRequestContext = (
    state: RootState
): AuroraInitializationData | undefined => {
    const user = selectCurrentUser(state);
    if (!user?.id) return undefined;

    // The existing Cognito refresh flow writes fresh tokens here. Selection
    // comes from this tab's Redux store, never another tab's stored selection.
    const stored = localStorage.getItem(
        constants.localStorageKeys.MCW_APP_INIT
    );
    if (!stored) return undefined;

    let appInit: unknown;
    try {
        appInit = JSON.parse(stored);
    } catch {
        return undefined;
    }
    if (!isRecord(appInit)) return undefined;
    const { data, requestHeaders } = appInit;
    if (
        !isRecord(data) ||
        !isRecord(data.userDetails) ||
        data.userDetails.id !== user.id ||
        !isNonemptyString(data.apiUrl) ||
        !isNonemptyString(data.auroraUrl) ||
        !isRecord(requestHeaders) ||
        !isNonemptyString(requestHeaders.authorization) ||
        !isNonemptyString(requestHeaders['x-oauth-idtoken'])
    ) {
        return undefined;
    }

    const clientIds = selectClientIds(state);
    const selectedClientIds = selectSelectedClientIds(state);
    if (
        !selectedClientIds.length ||
        selectedClientIds.some((id) => !clientIds.includes(id))
    ) {
        return undefined;
    }
    const allClientsSelected =
        selectedClientIds.length > 1 &&
        selectedClientIds.length === clientIds.length;
    const primaryClient =
        user.access?.find(({ client }) => selectedClientIds.includes(client))
            ?.client || selectedClientIds[0];

    return {
        data: {
            apiUrl: data.apiUrl,
            auroraUrl: data.auroraUrl,
            clientIds
        },
        requestHeaders: {
            authorization: requestHeaders.authorization,
            'x-oauth-idtoken': requestHeaders['x-oauth-idtoken'],
            'wise-client-id': allClientsSelected ? '' : primaryClient,
            'wise-client-ids': JSON.stringify(selectedClientIds)
        }
    };
};
